import { formatCurrency } from "../front/utils/currency";
import { ExternalListingConfiguration } from "./listingVariationsConfiguration";
import { PriceRoundingMethod } from "../codegen/graphql";
import { SupplierName } from "./suppliersConfig";
import { SALE_CHANNEL, SALES_CHANNELS } from "./salesChannels";
import { getTranslatedValue } from "./getTranslatedValue";

export type MARGIN_OR_TARGET_ENTITY_TYPE = 
  'listingConfig-variation'
  | 'variation'
  | 'listingConfig-typeValue'
  | 'typeValue'
  | 'listing'
  | 'ad'
  | 'default-listing'
  | 'default';

export type MARGIN_OR_TARGET_PRICE = 'marginPercentOnBaseCost' | 'targetListingPriceIncludingVat';

export type InheritedMarginOrTargetPrice = {
  entityType: MARGIN_OR_TARGET_ENTITY_TYPE;
  entityKey: string;
  entityLabel: string;
  marginPercentOnBaseCost: number | null;
  targetListingPriceIncludingVat: number | null;
  listingPriceRoundingMethod: PriceRoundingMethod | null;
}

// TODO: add config to type values
export default function inheritMarginOrTargetPrice({
  listing,
  listingConfiguration,
  variation,
  ad,
}: {
  listing: {
    id: string;
    shop: {
      shopType: SALE_CHANNEL;
    };
  } | null;
  listingConfiguration: ExternalListingConfiguration | null;
  variation: {
    id: string;
    marginPercentOnBaseCost: number | null;
    targetListingPriceIncludingVat: number | null;
    listingPriceRoundingMethod: PriceRoundingMethod | null;
    variationTypeValues: {
      id: string;
      value: string;
      // marginPercentOnBaseCost: number | null; TODO
      // targetListingPriceIncludingVat: number | null; TODO
      type: {
        type: string;
      };
    }[];
  } | null;
  ad: {
    id: string;
    marginPercentOnBaseCost: number | null;
    targetListingPriceIncludingVat: number | null;
    listingPriceRoundingMethod: PriceRoundingMethod | null;
  } | null;
}): {
  selected: InheritedMarginOrTargetPrice;
  possibles: InheritedMarginOrTargetPrice[];
} {
  const possibles: InheritedMarginOrTargetPrice[] = [];
  
  if (variation && listingConfiguration) {
    const variationConfig = listingConfiguration.variationsConfig.variations[variation.id]!;
    possibles.push(
      {
        entityType: 'listingConfig-variation' as const,
        entityKey: `listingConfig-variation-${variation.id}`,
        entityLabel: 'Listing Variation',
        marginPercentOnBaseCost: variationConfig.marginPercentOnBaseCost,
        targetListingPriceIncludingVat: variationConfig.targetListingPriceIncludingVat,
        listingPriceRoundingMethod: variationConfig.listingPriceRoundingMethod,
      }
    );
  }

  if (variation) {
    possibles.push(
      {
        entityType: 'variation' as const,
        entityKey: `variation-${variation.id}`,
        entityLabel: 'Variation',
        marginPercentOnBaseCost: variation.marginPercentOnBaseCost ?? null,
        targetListingPriceIncludingVat: variation.targetListingPriceIncludingVat ?? null,
        listingPriceRoundingMethod: variation.listingPriceRoundingMethod ?? null,
      }
    );
  }

  if (variation && listingConfiguration) {
    const listingConfigTypeValues = variation.variationTypeValues.map(v => {
      const typeValueConfig = listingConfiguration.variationsConfig.values[v.id];
      return {
        entityType: 'listingConfig-typeValue' as const,
        entityKey: `listingConfig-typeValue-${v.id}`,
        entityLabel: `Listing Type`,
        marginPercentOnBaseCost: typeValueConfig.marginPercentOnBaseCost,
        targetListingPriceIncludingVat: typeValueConfig.targetListingPriceIncludingVat,
        listingPriceRoundingMethod: typeValueConfig.listingPriceRoundingMethod,
      }
    });

    // const typeValues = variation.variationTypeValues.map(v => {
    //   return {
    //     entityType: 'typeValue' as const,
    //     entityKey: `typeValue-${v.id}`,
    //     entityLabel: `Type ${v.type.type}, ${v.value}`,
    //     marginPercentOnBaseCost: v.marginPercentOnBaseCost ?? null,
    //     targetListingPriceIncludingVat: v.targetListingPriceIncludingVat ?? null,
    //   }
    // });

    possibles.push(...listingConfigTypeValues);
    // possibles.push(...typeValues);
  }

  if (listing && listingConfiguration) {
    possibles.push({
      entityType: 'listing' as const,
      entityKey: `listing-${listing.id}`,
      entityLabel: 'Listing',
      marginPercentOnBaseCost: listingConfiguration.marginPercentOnBaseCost,
      targetListingPriceIncludingVat: listingConfiguration.targetListingPriceIncludingVat,
      listingPriceRoundingMethod: listingConfiguration.listingPriceRoundingMethod,
    });
  }

  if (ad) {
    possibles.push({
      entityType: 'ad' as const,
      entityKey: `ad-${ad.id}`,
      entityLabel: 'Ad',
      marginPercentOnBaseCost: ad.marginPercentOnBaseCost ?? null,
      targetListingPriceIncludingVat: ad.targetListingPriceIncludingVat ?? null,
      listingPriceRoundingMethod: ad.listingPriceRoundingMethod ?? null,
    });
  }

  if (listing && listingConfiguration) {
    const DEFAULT_MARGIN_PERCENT_ON_BASE_COST = {
      [SupplierName.FR_ilyes]: 40,
      [SupplierName.aliexpress]: 15,
    }[listingConfiguration.supplierKey!];

    const DEFAULT_ROUNDING: Record<SALE_CHANNEL, PriceRoundingMethod | null> = {
      ebay: PriceRoundingMethod.NearestFiftyCents,
      temu: null,
      leboncoin: PriceRoundingMethod.NearestFiftyCents,
      'direct-sale': PriceRoundingMethod.NearestFiftyCents,
    };

    possibles.push({
      entityType: 'default-listing' as const,
      entityKey: `default-listing-${listing.id}`,
      entityLabel: 'Default Listing',
      marginPercentOnBaseCost: DEFAULT_MARGIN_PERCENT_ON_BASE_COST,
      targetListingPriceIncludingVat: null,
      listingPriceRoundingMethod: DEFAULT_ROUNDING[listing.shop.shopType],
    });
  }

  possibles.push({
    entityType: 'default' as const,
    entityKey: `default`,
    entityLabel: 'Default',
    marginPercentOnBaseCost: 20,
    targetListingPriceIncludingVat: null,
    listingPriceRoundingMethod: null,
  });

  return {
    selected: possibles.find((possible) => (
      !!possible.marginPercentOnBaseCost
      || !!possible.targetListingPriceIncludingVat
    ))!,
    possibles,
  };
}

export function marginOrTargetPrice_getValueText(
  marginOrTargetPrice: InheritedMarginOrTargetPrice,
): string {
  if (marginOrTargetPrice.marginPercentOnBaseCost) {
    return `Base cost x ${marginOrTargetPrice.marginPercentOnBaseCost!}%`;
  }
  else if (marginOrTargetPrice.targetListingPriceIncludingVat) {
    return `Fixed price = ${formatCurrency(marginOrTargetPrice.targetListingPriceIncludingVat!, 'EUR')}`;
  }
  return 'No value set';
}

export function marginOrTargetPrice_getValueText_margin(
  marginPercentOnBaseCost: number | null,
) {
  return `Base cost x ${marginPercentOnBaseCost ?? 0}%`;
}

export function marginOrTargetPrice_getValueText_target(
  targetListingPriceIncludingVat: number | null,
) {
  return `Fixed price = ${formatCurrency(targetListingPriceIncludingVat ?? 0, 'EUR')}`;
}